import React from "react";
import { Alert, Button, Collapse, Nav, Spinner } from "react-bootstrap";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { AppState } from "../../store/root.reducer";
import User from "../../models/user.model";
import { AdminEdmArtworkDetailPage } from "../admin/edm/artwork.detail";
import { NewArtworkModal } from "../../components/modals/new.artwork";
import { Artwork } from "../../types/artworksData";
import { ApiClient } from "../../utils/apiClient";
import { ArtworkWorkspaceArtworkDetail } from "../../types/artworkWorkspace";
import { WorkspaceMediaPreview } from "../../components/artwork-workspace/WorkspaceMediaPreview";
import workspaceTheme from "../../components/artwork-workspace/workspaceTheme.module.css";
import { canAccessArtworkWorkspace, getNormalizedUserRoles, hasEdmCuratorCapability } from "../../utils/userAccess";

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);
type WorkspaceFeedback = { variant: "success" | "danger"; message: string } | null;
type DetailTabId = "artwork" | "publishing";
type PublishingSegmentId = "core-metadata" | "context" | "delivery" | "process";

const WORKSPACE_DETAIL_TABS: DetailTabId[] = ["artwork", "publishing"];
const WORKSPACE_PUBLISHING_SEGMENTS: PublishingSegmentId[] = ["core-metadata", "context", "delivery", "process"];

const isDetailTabId = (value: string | null): value is DetailTabId =>
  Boolean(value) && WORKSPACE_DETAIL_TABS.includes(value as DetailTabId);

const isPublishingSegmentId = (value: string | null): value is PublishingSegmentId =>
  Boolean(value) && WORKSPACE_PUBLISHING_SEGMENTS.includes(value as PublishingSegmentId);

const useWorkspaceAccess = () => {
  const user = useSelector((state: AppState) => state.auth.user as User | null);
  const roles = getNormalizedUserRoles(user);
  const curatorAccess = hasEdmCuratorCapability(user);
  return { user, roles, curatorAccess };
};

type WorkspaceBaseArtworkEditorPanelProps = {
  artworkId?: string;
  title?: string;
  helperText?: string;
  showRawJson?: boolean;
  hidePreview?: boolean;
  onArtworkSaved?: () => void;
};

const WorkspaceBaseArtworkEditorPanel = ({
  artworkId,
  title,
  helperText,
  showRawJson = false,
  hidePreview = false,
  onArtworkSaved,
}: WorkspaceBaseArtworkEditorPanelProps) => {
  const navigate = useNavigate();
  const isCreateMode = !artworkId;
  const [loading, setLoading] = React.useState<boolean>(!isCreateMode);
  const [artwork, setArtwork] = React.useState<Artwork | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<WorkspaceFeedback>(null);
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;

    if (!artworkId) {
      setArtwork(null);
      setLoadError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadArtwork = async () => {
      setLoading(true);
      setLoadError(null);

      const response = await apiClient.getArtworkById(artworkId);
      if (cancelled) {
        return;
      }

      if (response?.status && response?.artwork) {
        setArtwork(response.artwork as Artwork);
        setLoading(false);
        return;
      }

      setLoadError("Artwork could not be loaded in the artwork editor.");
      setLoading(false);
    };

    void loadArtwork();

    return () => {
      cancelled = true;
    };
  }, [artworkId]);

  const handleCompleted = React.useCallback((edited: boolean, artworkPayload: Artwork | undefined) => {
    if (!edited && artworkPayload?._id) {
      navigate(`/artwork/list/${artworkPayload._id}`, { replace: true });
      return;
    }

    if (edited && artworkPayload) {
      setArtwork(artworkPayload);
      onArtworkSaved?.();
    }
  }, [navigate, onArtworkSaved]);

  if (loading) {
    return <Spinner animation="border" />;
  }

  if (loadError) {
    return <Alert variant="danger">{loadError}</Alert>;
  }

  return (
    <div className={workspaceTheme.detailSection}>
      {title || helperText ? (
        <div className="d-flex flex-column gap-1 mb-3">
          {title ? <h3 className="h5 mb-0">{title}</h3> : null}
          {helperText ? <div className={workspaceTheme.accessNote}>{helperText}</div> : null}
        </div>
      ) : null}
      {feedback ? (
        <Alert
          variant={feedback.variant}
          className={`${workspaceTheme.surfaceAlert} ${workspaceTheme.feedbackAlert} ${feedback.variant === "success" ? workspaceTheme.feedbackSuccess : workspaceTheme.feedbackError}`}
          onClose={() => setFeedback(null)}
          dismissible
        >
          {feedback.message}
        </Alert>
      ) : null}
      <NewArtworkModal
        show
        inline
        hideInlineHeader
        hideInlinePreview={hidePreview}
        artworkToEdit={artwork ?? undefined}
        onCreatedHandler={handleCompleted}
        onSuccess={(message) => setFeedback({ variant: "success", message })}
        onFailed={(message) => setFeedback({ variant: "danger", message })}
      />
      {showRawJson && artwork ? (
        <div className="mt-4">
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced((current) => !current)}
          >
            {showAdvanced ? "Hide Advanced" : "Show Advanced"}
          </Button>
          <Collapse in={showAdvanced}>
            <div className={`${workspaceTheme.sheetBlock} mt-3`}>
              <div className={`${workspaceTheme.sheetLabel} mb-2`}>Raw Artwork JSON</div>
              <pre className={workspaceTheme.workspaceCodeBlock}>{JSON.stringify(artwork, null, 2)}</pre>
            </div>
          </Collapse>
        </div>
      ) : null}
    </div>
  );
};

type DetailSummaryProps = {
  detail: ArtworkWorkspaceArtworkDetail;
};

const DetailSummary = ({ detail }: DetailSummaryProps) => (
  <div className={workspaceTheme.detailSummarySurface}>
    <div className={workspaceTheme.summaryGrid}>
      <div className={workspaceTheme.summaryColumn}>
        <WorkspaceMediaPreview
          type={detail.artwork.type}
          title={detail.title}
          previewUrl={detail.artwork.media.thumbnailUrl}
          dataUrl={detail.artwork.media.dataUrl}
          originalUrl={detail.artwork.media.originalUrl}
          runtimeMedia={detail.artwork.media.runtimeMedia}
          variant="detail"
        />
      </div>
      <div className={workspaceTheme.summaryColumn}>
        <div className={workspaceTheme.sheetBlock}>
          <div className={workspaceTheme.sheetLabel}>Artwork Type</div>
          <div className={workspaceTheme.sheetValue}>{detail.artwork.type || "Artwork"}</div>
        </div>
        <div className={workspaceTheme.sheetBlock}>
          <div className={workspaceTheme.sheetLabel}>Description</div>
          <div className={workspaceTheme.descriptionPreview}>
            {detail.artwork.description?.trim() ? detail.artwork.description.trim() : "No description added yet."}
          </div>
        </div>
        <div className={workspaceTheme.sheetBlock}>
          <div className={workspaceTheme.sheetLabel}>Owner</div>
          <div className={workspaceTheme.sheetValue}>{detail.artwork.owner?.name || detail.artwork.owner?.email || "Unknown owner"}</div>
        </div>
        <div className={workspaceTheme.sheetBlock}>
          <div className={workspaceTheme.sheetLabel}>GUID</div>
          <div className={workspaceTheme.sheetValue}>{detail.artwork.artworkId}</div>
        </div>
      </div>
    </div>
  </div>
);

const PublishingAccessNote = () => (
  <div className={workspaceTheme.lockedPublishingCard}>
    <div className={workspaceTheme.sheetLabel}>Publishing</div>
    <div className={workspaceTheme.sheetValue}>Activate curator to view publishing information.</div>
  </div>
);

export const ArtworkWorkspaceCreateEditor = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useWorkspaceAccess();
  const [feedback, setFeedback] = React.useState<WorkspaceFeedback>(null);
  const initialOptimizationJobId = searchParams.get("optimizationJob");

  const clearRestartJob = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("optimizationJob");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!canAccessArtworkWorkspace(user)) {
    return null;
  }

  return (
    <div className={workspaceTheme.detailSection}>
      {feedback ? (
        <Alert
          variant={feedback.variant}
          className={`${workspaceTheme.surfaceAlert} ${workspaceTheme.feedbackAlert} ${feedback.variant === "success" ? workspaceTheme.feedbackSuccess : workspaceTheme.feedbackError}`}
          onClose={() => setFeedback(null)}
          dismissible
        >
          {feedback.message}
        </Alert>
      ) : null}
      <NewArtworkModal
        show
        inline
        hideInlineHeader
        initialOptimizationJobId={initialOptimizationJobId}
        onOptimizationRestartPrepared={clearRestartJob}
        onCreatedHandler={(_edited, artworkPayload) => {
          if (artworkPayload?._id) {
            navigate(`/artwork/list/${artworkPayload._id}`, { replace: true });
            return;
          }
          navigate("/artwork/list?tab=artworks", { replace: true });
        }}
        onSuccess={(message) => setFeedback({ variant: "success", message })}
        onFailed={(message) => setFeedback({ variant: "danger", message })}
      />
    </div>
  );
};

export const ArtworkWorkspaceDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, roles, curatorAccess } = useWorkspaceAccess();
  const artworkId = params.artworkId;
  const [loading, setLoading] = React.useState(Boolean(artworkId));
  const [error, setError] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<ArtworkWorkspaceArtworkDetail | null>(null);
  const [detailReloadToken, setDetailReloadToken] = React.useState(0);
  const tabContentRef = React.useRef<HTMLDivElement | null>(null);

  const activeTab = React.useMemo<DetailTabId>(() => {
    const rawDetailTab = searchParams.get("detailTab");
    const rawPublishingSegment = searchParams.get("publishingSegment");

    if (!curatorAccess) {
      return "artwork";
    }

    if (rawDetailTab === "publishing") {
      return "publishing";
    }

    if (!rawDetailTab && isPublishingSegmentId(rawPublishingSegment)) {
      return "publishing";
    }

    return "artwork";
  }, [curatorAccess, searchParams]);

  const activePublishingSegment = React.useMemo<PublishingSegmentId>(() => {
    const rawSegment = searchParams.get("publishingSegment");
    return isPublishingSegmentId(rawSegment) ? rawSegment : "core-metadata";
  }, [searchParams]);

  const reloadDetail = React.useCallback(() => {
    setDetailReloadToken((current) => current + 1);
  }, []);

  React.useEffect(() => {
    const rawDetailTab = searchParams.get("detailTab");
    const rawPublishingSegment = searchParams.get("publishingSegment");
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (!curatorAccess) {
      if (rawDetailTab) {
        next.delete("detailTab");
        changed = true;
      }
      if (rawPublishingSegment) {
        next.delete("publishingSegment");
        changed = true;
      }
    } else {
      if (rawDetailTab && !isDetailTabId(rawDetailTab)) {
        next.delete("detailTab");
        changed = true;
      }

      if (rawPublishingSegment && !isPublishingSegmentId(rawPublishingSegment)) {
        next.delete("publishingSegment");
        changed = true;
      }

      if (activeTab !== "publishing" && next.has("publishingSegment")) {
        next.delete("publishingSegment");
        changed = true;
      }

      if (activeTab === "publishing") {
        if (next.get("detailTab") !== "publishing") {
          next.set("detailTab", "publishing");
          changed = true;
        }
        if (next.get("publishingSegment") !== activePublishingSegment) {
          next.set("publishingSegment", activePublishingSegment);
          changed = true;
        }
      }
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [activePublishingSegment, activeTab, curatorAccess, searchParams, setSearchParams]);

  const setActiveTab = React.useCallback((nextTab: DetailTabId) => {
    const next = new URLSearchParams(searchParams);

    if (nextTab === "publishing" && curatorAccess) {
      next.set("detailTab", "publishing");
      next.set("publishingSegment", activePublishingSegment);
    } else {
      next.delete("detailTab");
      next.delete("publishingSegment");
    }

    setSearchParams(next, { replace: true });
  }, [activePublishingSegment, curatorAccess, searchParams, setSearchParams]);

  const setPublishingSegment = React.useCallback((nextSegment: PublishingSegmentId) => {
    if (!curatorAccess) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("detailTab", "publishing");
    next.set("publishingSegment", nextSegment);
    setSearchParams(next, { replace: true });
  }, [curatorAccess, searchParams, setSearchParams]);

  React.useEffect(() => {
    let cancelled = false;
    if (!artworkId) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      const response = await apiClient.getArtworkWorkspaceArtwork(artworkId);
      if (cancelled) {
        return;
      }
      if (!response.status) {
        setError(response.error?.message || "Failed to load artwork.");
        setDetail(null);
        setLoading(false);
        return;
      }
      setDetail(response.data);
      setLoading(false);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [artworkId, detailReloadToken]);

  React.useEffect(() => {
    if (!artworkId || !detail || !tabContentRef.current) {
      return;
    }

    const target = tabContentRef.current;
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, artworkId, detail?.artworkId]);

  if (!canAccessArtworkWorkspace(user)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container py-4">
      <div className={workspaceTheme.workspaceShell}>
        <div className={`d-flex justify-content-between gap-3 mb-4 ${workspaceTheme.hero}`}>
          <div>
            <h2 className="mb-1">{artworkId ? (detail?.title || "Artwork Details") : "Add Artwork"}</h2>
            {detail ? (
              <>
                <div className={workspaceTheme.neutralText}>{detail.artistName || "Unknown creator"}</div>
                <div className={workspaceTheme.secondaryText}>GUID: {detail.artwork.artworkId}</div>
              </>
            ) : null}
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
              variant="light"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
              onClick={() => navigate("/artwork/list?tab=artworks")}
            >
              Back to Artworks
            </Button>
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div className="py-5 text-center"><Spinner animation="border" /></div>
        ) : artworkId && detail ? (
          <div className="d-flex flex-column gap-4">
            <DetailSummary detail={detail} />

            <div className={workspaceTheme.detailSection} ref={tabContentRef}>
              <Nav
                variant="tabs"
                activeKey={activeTab}
                className={`${workspaceTheme.topTabs} mb-3`}
                onSelect={(eventKey) => setActiveTab((eventKey as DetailTabId) || "artwork")}
              >
                <Nav.Item>
                  <Nav.Link eventKey="artwork">Artwork</Nav.Link>
                </Nav.Item>
                {curatorAccess ? (
                  <Nav.Item>
                    <Nav.Link eventKey="publishing">Publishing</Nav.Link>
                  </Nav.Item>
                ) : null}
              </Nav>

              {!curatorAccess ? <PublishingAccessNote /> : null}

              {activeTab === "artwork" ? (
                <WorkspaceBaseArtworkEditorPanel
                  artworkId={artworkId}
                  title="Artwork Details"
                  helperText={curatorAccess
                    ? "Manage the shared artwork information used across the workspace."
                    : "Manage the artwork information available on your current account."
                  }
                  showRawJson={curatorAccess}
                  hidePreview
                  onArtworkSaved={reloadDetail}
                />
              ) : null}

              {curatorAccess && activeTab === "publishing" ? (
                <AdminEdmArtworkDetailPage
                  embedded
                  activeSegment={activePublishingSegment}
                  onSegmentChange={setPublishingSegment}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <ArtworkWorkspaceCreateEditor />
        )}
      </div>
    </div>
  );
};
